import React, { useState } from "react";
import Client from "../../models/client";
import useSWR from "swr";
import { createPageGroup, deletePageGroupWithChildren, FETCH_KEYS, fetcherWithAuth } from "../api";
import { PageGroupData } from "../utils/switchoverCheck";
import { notifications } from "@mantine/notifications";
import BigSpinning from "./BigSpinning";
import { ActionIcon, Badge, Box, Button, Group, ScrollArea, Stack, Table, Text, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { FilesIcon, PencilSimpleIcon, PlayCircleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { BaseModal } from "../components/BaseModal";
import TemplatePageFormModal from "../components/TemplatePageFormModal";
import { TemplatePageType } from "../utils/templatePageForm";

interface Props {
  client: Client
}

const CampaignsContainer: React.FC<Props> = ({ client }) => {
  const { data, error, mutate } = useSWR(FETCH_KEYS.pageGroups(client.id), fetcherWithAuth)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [pageGroup, setPageGroup] = useState<PageGroupData[] | null>(null)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // 閉じるアニメーション中もタイトルが変わらないよう、editTarget は残したまま opened だけ倒す
  const [editTarget, setEditTarget] = useState<{ id: number; type: TemplatePageType } | null>(null)
  const [editOpened, setEditOpened] = useState(false)

  const items: PageGroupData[] = data?.page_groups ?? []

  // common の変更は子の invite / guest に同期されるため、紐づく行の公開状態も見る
  const handleClickEditButton = (id: number, type: TemplatePageType) => {
    const affectsPublished = items.some(
      (item) => (item.id === id || item.campaign_id === id) && item.enabled,
    )
    if (
      affectsPublished &&
      !window.confirm('保存した変更は公開中のページに即時反映されます。編集を続けますか？')
    ) {
      return
    }
    setEditTarget({ id, type })
    setEditOpened(true)
  }

  // 紹介ページ共通設定（campaign）の行
  const campaigns = items.filter((row) => row.name === 'campaign')

  // 共通設定に紐づく紹介者（invite）・友人（guest）ページの行
  const relatedPages = items.filter((row) => row.name !== 'campaign' && row.campaign_id)

  const handleClickAddButton = async () => {
    if (creating) return
    setCreating(true)

    try {
      await createPageGroup(client.id, 'campaign', 0)
      await mutate()
      notifications.show({ message: '共通設定を作成しました', color: 'teal' })
    } catch (e) {
      console.error('共通設定の作成に失敗しました', e)
      notifications.show({ message: '共通設定の作成に失敗しました', color: 'red' })
    } finally {
      setCreating(false)
    }
  }

  // 共通設定の削除では、紐づく invite/guest ページも一括削除される
  const handleClickDeleteButton = (id: number) => {
    const targets = items
      .filter((item) => item.id === id || item.campaign_id === id)
      .sort((a, b) => a.id - b.id)
    if (targets.length === 0) return
    setPageGroup(targets)
    setDeleteTargetId(id)
    setIsDialogOpen(true)
  }

  const handleDeletePageGroup = async () => {
    if (deleteTargetId === null || deleting) return
    setDeleting(true)

    try {
      await deletePageGroupWithChildren(client.id, deleteTargetId)
      setIsDialogOpen(false)
      await mutate()
      notifications.show({ message: 'ページを削除しました', color: 'teal' })
    } catch (e) {
      console.error('ページの削除に失敗しました', e)
      notifications.show({ message: 'ページの削除に失敗しました', color: 'red' })
    } finally {
      setDeleting(false)
    }
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  if (error) return <div>Failed to load</div>
  if (!data) return <BigSpinning />

  return (
    <Box>
      <Group gap={12} mb={40}>
        <ThemeIcon variant="transparent" color="#5DA327">
          <FilesIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          ページ一覧
        </Title>
      </Group>

      {campaigns.length === 0 && (
        <Button
          variant="filled"
          radius="xl"
          leftSection={<PlusIcon size={16} weight="bold" />}
          onClick={handleClickAddButton}
          loading={creating}
        >
          共通設定をはじめる
        </Button>
      )}

      {campaigns.length > 0 && (
        <>
          <Title order={2} size={18} fw={600} mb={16}>
            紹介ページ共通設定
          </Title>

          <Box style={{ border: '1px solid #0000001A', borderRadius: 8, overflow: 'hidden' }}>
            <Table withColumnBorders styles={{ th: { backgroundColor: '#00000008' } }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>ID</Table.Th>
                  <Table.Th w={120}>種類</Table.Th>
                  <Table.Th>説明</Table.Th>
                  <Table.Th w={80} ta="center">編集</Table.Th>
                  <Table.Th w={80} ta="center">削除</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {campaigns.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>{row.id}</Table.Td>
                    <Table.Td>common</Table.Td>
                    <Table.Td>{row.page_description}</Table.Td>
                    <Table.Td ta="center">
                      <Tooltip label="編集">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => handleClickEditButton(row.id, 'common')}
                        >
                          <PencilSimpleIcon size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Tooltip label={row.enabled ? '公開中のページは削除できません' : '削除'}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          data-disabled={row.enabled || undefined}
                          onClick={(e) => {
                            if (row.enabled) {
                              e.preventDefault()
                              return
                            }
                            handleClickDeleteButton(row.id)
                          }}
                        >
                          <TrashIcon size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>

          <Title order={2} size={18} fw={600} mt={40} mb={16}>
            紹介者（inviter）ページと友人（guest）ページ設定
          </Title>

          <Box style={{ border: '1px solid #0000001A', borderRadius: 8, overflow: 'hidden' }}>
            <Table withColumnBorders styles={{ th: { backgroundColor: '#00000008' } }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>ID</Table.Th>
                  <Table.Th w={180}>種類</Table.Th>
                  <Table.Th>説明</Table.Th>
                  <Table.Th w={110} ta="center">プレビュー</Table.Th>
                  <Table.Th w={110} ta="center">ステータス</Table.Th>
                  <Table.Th w={80} ta="center">編集</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {relatedPages.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="#00000099" ta="center" py="md">
                        ページが登録されていません
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  relatedPages.map((row) => (
                    <Table.Tr key={row.id}>
                      <Table.Td>{row.id}</Table.Td>
                      <Table.Td>{row.name}</Table.Td>
                      <Table.Td>{row.page_description}</Table.Td>
                      <Table.Td ta="center">
                        <Tooltip label="プレビュー">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            component="a"
                            href={`/${client.url_name}/preview/${row.analytics_html}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <PlayCircleIcon size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td ta="center">
                        <Badge color={row.enabled ? 'invy' : 'gray'} variant="light">
                          {row.enabled ? '公開中' : '非公開'}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="center">
                        <Tooltip label="編集">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() =>
                              handleClickEditButton(row.id, row.name === 'invite' ? 'inviter' : 'guest')
                            }
                          >
                            <PencilSimpleIcon size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </>
      )}

      {pageGroup && (
        <BaseModal
          opened={isDialogOpen}
          onClose={handleCloseDialog}
          title="ページの削除"
          width={600}
          size={400}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008">
            <Stack gap="sm" p="md">
              <Text>以下のページを削除します。よろしいですか？</Text>
              <Stack gap={4}>
                {pageGroup.map((row) => (
                  <Text key={row.id} size="sm">
                    ・ID: {row.id}（{row.name === 'campaign' ? 'common' : row.name}）
                  </Text>
                ))}
              </Stack>
              <Text c="red" size="sm">
                ※一度削除すると元に戻せません。
              </Text>
            </Stack>
          </ScrollArea>

          <Box
            p="md"
            style={{
              borderTop: '1px solid #0000001A',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Group justify="flex-end" gap={8}>
              <Button
                variant="outline"
                radius="xl"
                color="gray"
                onClick={handleCloseDialog}
                disabled={deleting}
              >
                キャンセル
              </Button>
              <Button
                variant="filled"
                radius="xl"
                onClick={handleDeletePageGroup}
                loading={deleting}
              >
                削除
              </Button>
            </Group>
          </Box>
        </BaseModal>
      )}

      {editTarget && (
        <TemplatePageFormModal
          opened={editOpened}
          onClose={() => setEditOpened(false)}
          client={client}
          pageGroupId={editTarget.id}
          templatePageType={editTarget.type}
          onSaved={() => mutate()}
        />
      )}
    </Box>
  )
}

export default CampaignsContainer
